/**
 * Showing requests — client property-viewing requests (Phase 11-B).
 *
 * All three staff roles can open this; the backend scopes rows server-side
 * (agents see only requests assigned to them, admins the tenant). Confirm /
 * Cancel ride PATCH /appointment/requests/{id} and refresh both this list and
 * the header bell count. Confirming emails + texts the client back.
 *
 * Two views on one page: the filterable REQUEST queue on top, and an
 * always-on "Upcoming showings" list (confirmed + still ahead) so the agent
 * sees what's next without switching tabs. Once a confirmed showing's time has
 * passed the agent marks the outcome — "Client showed" or "No-show" — which
 * completes it with that outcome, shown as a badge in the Completed list.
 */

import { type ReactNode, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiClientError } from "@re-platform/api-client";
import { Button } from "@re-platform/ui";

import {
  actingClientFor,
  brokerageLabel,
  useMergedQuery,
  type Scoped,
} from "../../lib/mergedQuery";
import { useMemberships } from "../../lib/memberships";

type Outcome = "ATTENDED" | "NO_SHOW";

export interface AppointmentRow {
  id: string;
  status: string;
  appointment_type: string;
  property_address: string | null;
  starts_at: string;
  client_id: string | null;
  client_name: string | null;
  agent_name: string | null;
  created_at: string;
  outcome?: Outcome | null;
}

type Filter = "PENDING" | "CONFIRMED" | "COMPLETED" | "all";
const FILTERS: readonly Filter[] = ["PENDING", "CONFIRMED", "COMPLETED", "all"];
const FILTER_LABEL: Record<Filter, string> = {
  PENDING: "Pending",
  CONFIRMED: "Confirmed",
  COMPLETED: "Completed",
  all: "All",
};

const LIVE = new Set(["PENDING", "PROPOSED", "SCHEDULED", "CONFIRMED"]);

/** "Sat, Jul 25" */
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}
/** "2:00 PM" */
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Outcome badge for a completed showing. */
function OutcomeBadge({ outcome }: { outcome?: Outcome | null }): ReactNode {
  const map = {
    ATTENDED: {
      label: "Showed",
      bg: "var(--rp-color-success-faint)",
      fg: "var(--rp-color-success)",
    },
    NO_SHOW: {
      label: "No-show",
      bg: "var(--rp-color-danger-faint)",
      fg: "var(--rp-color-danger)",
    },
  } as const;
  const m =
    (outcome && map[outcome]) ||
    ({
      label: "Completed",
      bg: "var(--rp-color-surface-2)",
      fg: "var(--rp-color-text-muted)",
    } as const);
  return (
    <span
      style={{
        background: m.bg,
        color: m.fg,
        fontSize: 12.5,
        fontWeight: 600,
        padding: "4px 11px",
        borderRadius: 999,
      }}
    >
      {m.label}
    </span>
  );
}

export function Appointments() {
  const [filter, setFilter] = useState<Filter>("PENDING");
  const [outcomeFilter, setOutcomeFilter] = useState<"all" | Outcome>("all");
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Dual-licensed staff see BOTH brokerages merged (PR 5); single-membership
  // staff take the plain path unchanged. We fetch the whole set once and derive
  // both the filtered queue and the upcoming list client-side.
  const memberships = useMemberships();
  const tenants = memberships.data ?? [];
  const query = useMergedQuery<AppointmentRow>(
    tenants,
    ["appointments"],
    (client) => client.request<AppointmentRow[]>("/appointment/requests"),
    { enabled: tenants.length > 0 },
  );

  // V053 — the agent proposes a new time; the CLIENT then accepts it (the
  // appointment becomes PROPOSED, not CONFIRMED: we do not move a client onto a
  // slot they have not agreed to).
  const [proposing, setProposing] = useState<string | null>(null);
  const [proposedAt, setProposedAt] = useState("");

  const reschedule = useMutation({
    mutationFn: ({
      id,
      startsAt,
      tenantId,
    }: {
      id: string;
      startsAt: string;
      tenantId: string;
    }) =>
      actingClientFor(tenantId, query.isDual).request(
        `/appointment/requests/${id}/reschedule`,
        {
          method: "PATCH",
          body: { starts_at: new Date(startsAt).toISOString() },
        },
      ),
    onSuccess: async () => {
      setProposing(null);
      await queryClient.invalidateQueries({ queryKey: ["appointments"] });
    },
  });

  // Three consecutive late changes pause a client's ability to book; only an
  // agent can lift it, and doing so forgives the streak so far.
  const reenable = useMutation({
    mutationFn: ({
      clientId,
      tenantId,
    }: {
      clientId: string;
      tenantId: string;
    }) =>
      actingClientFor(tenantId, query.isDual).request(
        `/appointment/clients/${clientId}/showings`,
        { method: "PATCH", body: { enabled: true } },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["appointments"] });
    },
  });

  const mutation = useMutation({
    mutationFn: ({
      id,
      status,
      outcome,
      tenantId,
    }: {
      id: string;
      status: "CONFIRMED" | "CANCELLED" | "COMPLETED";
      outcome?: Outcome;
      tenantId: string;
    }) =>
      actingClientFor(tenantId, query.isDual).request<AppointmentRow>(
        `/appointment/requests/${id}`,
        {
          method: "PATCH",
          body: { status, ...(outcome ? { outcome } : {}) },
        },
      ),
    onSuccess: async () => {
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["appointments"] }),
        queryClient.invalidateQueries({ queryKey: ["notifications-summary"] }),
      ]);
    },
    onError: (err) =>
      setError(
        err instanceof ApiClientError
          ? err.message
          : "Could not update the appointment. Please try again.",
      ),
  });

  const now = Date.now();

  // Request queue — filtered by the active tab. Next showing first, finished
  // ones below; re-sorted across the MERGED set.
  const baseFiltered =
    filter === "all"
      ? query.rows
      : query.rows.filter((r) => r.row.status === filter);
  // Completed can be narrowed by outcome (Showed vs No-show).
  const filtered =
    filter === "COMPLETED" && outcomeFilter !== "all"
      ? baseFiltered.filter((r) => r.row.outcome === outcomeFilter)
      : baseFiltered;
  const requestRows = [...filtered].sort((a, b) => {
    const aLive = LIVE.has(a.row.status);
    const bLive = LIVE.has(b.row.status);
    if (aLive !== bLive) return aLive ? -1 : 1;
    const at = new Date(a.row.starts_at).getTime();
    const bt = new Date(b.row.starts_at).getTime();
    return aLive ? at - bt : bt - at;
  });

  // Upcoming showings — confirmed and still ahead, soonest first. Always shown
  // regardless of the active filter.
  const upcoming = query.rows
    .filter(
      (r) =>
        r.row.status === "CONFIRMED" &&
        new Date(r.row.starts_at).getTime() > now,
    )
    .sort(
      (a, b) =>
        new Date(a.row.starts_at).getTime() -
        new Date(b.row.starts_at).getTime(),
    );

  function renderCard({ row: a, tenant }: Scoped<AppointmentRow>) {
    const isPast = new Date(a.starts_at).getTime() <= now;
    return (
      <div
        key={`${tenant.tenant_id}:${a.id}`}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
          padding: "16px 18px",
          border: "1px solid var(--rp-color-border)",
          borderRadius: "var(--rp-radius-lg)",
          background: "var(--rp-color-surface)",
          boxShadow: "var(--rp-shadow-sm)",
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 16,
              color: "var(--rp-color-text)",
            }}
          >
            {a.property_address ?? "Property showing"}
          </div>
          <div
            style={{
              color: "var(--rp-color-text-muted)",
              fontSize: 13.5,
              marginTop: 2,
            }}
          >
            {a.client_name ?? "Client"} · requested {fmtDate(a.starts_at)} ·{" "}
            {fmtTime(a.starts_at)}
            {query.isDual && ` · ${brokerageLabel(tenant)}`}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          {a.status === "COMPLETED" ? (
            <OutcomeBadge outcome={a.outcome} />
          ) : a.status === "CANCELLED" ? (
            <span
              style={{
                color: "var(--rp-color-text-muted)",
                fontSize: "0.85rem",
              }}
            >
              Cancelled
            </span>
          ) : proposing === a.id ? (
            <>
              <input
                type="datetime-local"
                value={proposedAt}
                onChange={(e) => setProposedAt(e.target.value)}
                aria-label="Proposed showing time"
                style={{ marginRight: 8 }}
              />
              <Button
                size="sm"
                variant="primary"
                disabled={!proposedAt || reschedule.isPending}
                onClick={() =>
                  reschedule.mutate({
                    id: a.id,
                    startsAt: proposedAt,
                    tenantId: tenant.tenant_id,
                  })
                }
              >
                {reschedule.isPending ? "Sending…" : "Propose"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setProposing(null)}
              >
                Back
              </Button>
            </>
          ) : a.status === "CONFIRMED" && isPast ? (
            // The showing's time has passed — record what actually happened
            // before it's completed. Both outcomes complete the appointment.
            <>
              <Button
                size="sm"
                variant="secondary"
                disabled={mutation.isPending}
                title="The client attended — complete this showing"
                onClick={() =>
                  mutation.mutate({
                    id: a.id,
                    status: "COMPLETED",
                    outcome: "ATTENDED",
                    tenantId: tenant.tenant_id,
                  })
                }
              >
                Client showed
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={mutation.isPending}
                title="The client didn't attend — complete as a no-show"
                onClick={() =>
                  mutation.mutate({
                    id: a.id,
                    status: "COMPLETED",
                    outcome: "NO_SHOW",
                    tenantId: tenant.tenant_id,
                  })
                }
              >
                No-show
              </Button>
            </>
          ) : (
            <>
              {a.status === "PENDING" && (
                <Button
                  size="sm"
                  variant="primary"
                  disabled={mutation.isPending}
                  onClick={() =>
                    mutation.mutate({
                      id: a.id,
                      status: "CONFIRMED",
                      tenantId: tenant.tenant_id,
                    })
                  }
                >
                  Confirm
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                disabled={mutation.isPending}
                onClick={() => {
                  setProposing(a.id);
                  setProposedAt("");
                }}
              >
                Propose new time
              </Button>
              {a.client_id && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={reenable.isPending}
                  title="Restore this client's ability to book showings after repeated late changes"
                  onClick={() =>
                    reenable.mutate({
                      clientId: a.client_id as string,
                      tenantId: tenant.tenant_id,
                    })
                  }
                >
                  Re-enable bookings
                </Button>
              )}
              <Button
                size="sm"
                variant="danger"
                disabled={mutation.isPending}
                onClick={() =>
                  mutation.mutate({
                    id: a.id,
                    status: "CANCELLED",
                    tenantId: tenant.tenant_id,
                  })
                }
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <header style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0 }}>Showing requests</h1>
        <p style={{ color: "var(--rp-color-text-muted)", marginTop: 4 }}>
          Property showings clients have requested — confirm a time and they're
          notified by email and text.
        </p>
      </header>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {FILTERS.map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? "primary" : "secondary"}
            onClick={() => setFilter(f)}
          >
            {FILTER_LABEL[f]}
          </Button>
        ))}
      </div>

      {filter === "COMPLETED" && (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <span
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: "var(--rp-color-text-muted)",
            }}
          >
            Outcome
          </span>
          {(
            [
              ["all", "All"],
              ["ATTENDED", "Showed"],
              ["NO_SHOW", "No-show"],
            ] as const
          ).map(([v, label]) => (
            <Button
              key={v}
              size="sm"
              variant={outcomeFilter === v ? "primary" : "ghost"}
              onClick={() => setOutcomeFilter(v)}
            >
              {label}
            </Button>
          ))}
        </div>
      )}

      {error && (
        <p style={{ color: "var(--rp-color-danger, red)", fontSize: 13 }}>
          {error}
        </p>
      )}

      {query.isPending ? (
        <p style={{ color: "var(--rp-color-text-muted)" }}>Loading…</p>
      ) : query.isError ? (
        <p style={{ color: "var(--rp-color-danger, red)", fontSize: 13 }}>
          {query.error instanceof Error
            ? query.error.message
            : String(query.error)}
        </p>
      ) : requestRows.length === 0 ? (
        <p style={{ color: "var(--rp-color-text-muted)" }}>
          No {filter === "all" ? "" : FILTER_LABEL[filter].toLowerCase() + " "}
          requests.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {requestRows.map(renderCard)}
        </div>
      )}

      {upcoming.length > 0 && (
        <section style={{ marginTop: 36 }}>
          <h2 style={{ margin: "0 0 2px", fontSize: 18 }}>Upcoming showings</h2>
          <p
            style={{
              color: "var(--rp-color-text-muted)",
              margin: "0 0 16px",
              fontSize: 13.5,
            }}
          >
            Confirmed and coming up — your next stops, soonest first.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {upcoming.map(renderCard)}
          </div>
        </section>
      )}
    </div>
  );
}
